import { useEffect, useState } from "react";
import { Loader2, LogOut, Calculator } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935] h-9 text-sm";
const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export function ExitDialog({ api, employee, onClose }) {
  const ex = employee.exit || {};
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({
    reason: ex.reason || "demission", end_date: ex.end_date || "", last_worked_date: ex.last_worked_date || "", notified_at: ex.notified_at || "",
    notice_status: ex.notice_status || "", notice_months: ex.notice_months ?? "", cp_days: ex.cp_days ?? "", cp_indemnity: ex.cp_indemnity ?? "",
    severance_amount: ex.severance_amount ?? "", precarity_applicable: ex.precarity_applicable, notes: ex.notes || "",
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/payroll/exit/reasons").then((r) => setReasons(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const body = () => {
    const b = { ...form };
    ["notice_months", "cp_days", "cp_indemnity", "severance_amount"].forEach((k) => { if (b[k] === "" || b[k] === null) delete b[k]; else b[k] = Number(b[k]); });
    if (!b.notice_status) delete b.notice_status;
    if (b.precarity_applicable === undefined || b.precarity_applicable === null) delete b.precarity_applicable;
    return b;
  };

  useEffect(() => {
    if (!form.end_date || form.end_date.length !== 10) { setPreview(null); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.post(`/payroll/employees/${employee.employee_id}/exit/preview`, body())
        .then((r) => setPreview(r.data)).catch(() => setPreview(null)).finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(t);
  }, [form.reason, form.end_date, form.notice_status, form.notice_months, form.cp_days, form.cp_indemnity, form.severance_amount, form.precarity_applicable]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.end_date) return toast.error("Date de fin de contrat requise");
    setSaving(true);
    try {
      await api.put(`/payroll/employees/${employee.employee_id}/exit`, body());
      toast.success("Sortie planifiée — le bulletin du mois de sortie intégrera le solde de tout compte");
      onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const p = preview;
  const showSeverance = p && p.severance_type !== "none";
  const noticeEligible = p && (p.notice_suggested_months > 0 || form.notice_status);

  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="exit-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-base"><LogOut className="w-4 h-4 text-[#f7c935]" /> Fin de contrat — {employee.first_name} {employee.last_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-3 space-y-3">
            <div>
              <label className="text-[11px] text-slate-400">Motif de rupture</label>
              <select value={form.reason} onChange={(e) => set("reason", e.target.value)} className={`${inputCls} w-full rounded px-2`} data-testid="exit-reason">
                {reasons.map((r) => <option key={r.code} value={r.code}>{`${r.label} (DSN ${r.dsn})`}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><label className="text-[11px] text-slate-400">Fin de contrat *</label><Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} data-testid="exit-end-date" /></div>
              <div><label className="text-[11px] text-slate-400">Dernier jour travaillé</label><Input type="date" value={form.last_worked_date} onChange={(e) => set("last_worked_date", e.target.value)} className={inputCls} data-testid="exit-last-worked" /></div>
              <div><label className="text-[11px] text-slate-400">Notification / accord</label><Input type="date" value={form.notified_at} onChange={(e) => set("notified_at", e.target.value)} className={inputCls} data-testid="exit-notified-at" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-slate-400">Préavis {p ? `(suggéré : ${p.notice_suggested_months} mois)` : ""}</label>
                <select value={form.notice_status} onChange={(e) => set("notice_status", e.target.value)} className={`${inputCls} w-full rounded px-2`} data-testid="exit-notice-status">
                  <option value="">Automatique</option>
                  <option value="worked">Effectué (pas d'indemnité)</option>
                  <option value="paid">Non effectué — payé (indemnité compensatrice)</option>
                  <option value="waived">Non effectué — non payé</option>
                  <option value="none">Sans préavis</option>
                </select>
              </div>
              <div><label className="text-[11px] text-slate-400">Durée du préavis (mois)</label><Input type="number" step="0.5" value={form.notice_months} onChange={(e) => set("notice_months", e.target.value)} placeholder={p ? String(p.notice_suggested_months) : ""} className={inputCls} data-testid="exit-notice-months" disabled={!noticeEligible && form.notice_status !== "paid"} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[11px] text-slate-400">Congés payés restants (jours)</label><Input type="number" step="0.01" value={form.cp_days} onChange={(e) => set("cp_days", e.target.value)} placeholder={p ? String(p.cp_days) : ""} className={inputCls} data-testid="exit-cp-days" /></div>
              <div><label className="text-[11px] text-slate-400">Indemnité compensatrice CP (€) {p ? `— maintien ${eur(p.cp_methods?.maintien)}${p.cp_methods?.dixieme ? ` / 1/10e ${eur(p.cp_methods.dixieme)}` : ""}` : ""}</label><Input type="number" step="0.01" value={form.cp_indemnity} onChange={(e) => set("cp_indemnity", e.target.value)} placeholder={p ? String(p.cp_methods?.recommended) : ""} className={inputCls} data-testid="exit-cp-indemnity" /></div>
            </div>
            {showSeverance && (
              <div>
                <label className="text-[11px] text-slate-400">Indemnité de rupture (€) — légale : <span className="text-[#f7c935] font-mono">{eur(p.severance_legal)}</span> {p.severance_type === "rupture_conventionnelle" && "(minimum pour une rupture conventionnelle)"}</label>
                <Input type="number" step="0.01" value={form.severance_amount} onChange={(e) => set("severance_amount", e.target.value)} placeholder={String(p.severance_legal)} className={inputCls} data-testid="exit-severance-amount" />
              </div>
            )}
            {p && (employee.contract?.type === "CDD") && (
              <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                <input type="checkbox" checked={form.precarity_applicable ?? p.precarity_applicable} onChange={(e) => set("precarity_applicable", e.target.checked)} className="accent-[#f7c935]" data-testid="exit-precarity-toggle" />
                Indemnité de fin de contrat CDD (10 %) applicable {p.precarity_applicable ? `— base ${eur(p.precarity_base)}` : ""}
              </label>
            )}
            <div><label className="text-[11px] text-slate-400">Notes (référence de l'accord, remarques…)</label><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} data-testid="exit-notes" /></div>
          </div>
          <div className="md:col-span-2">
            <div className="bg-[#0b0811] border border-white/5 rounded-xl p-3 text-xs space-y-1.5 sticky top-0" data-testid="exit-preview">
              <div className="flex items-center gap-2 text-slate-400 uppercase tracking-wider text-[10px] font-bold"><Calculator className="w-3.5 h-3.5" /> Estimation {loading && <Loader2 className="w-3 h-3 animate-spin" />}</div>
              {!p ? <div className="text-slate-500">Renseignez la date de fin pour obtenir l'estimation.</div> : (
                <>
                  <Row l="Ancienneté" v={p.seniority_label} />
                  <Row l="Salaire de référence" v={eur(p.reference_salary?.reference)} hint={`${p.reference_salary?.months_history || 0} mois d'historique`} />
                  <Row l={`Salaire du mois (prorata ${Math.round(p.prorata * 100)} %)`} v={eur((employee.contract?.gross_monthly || 0) * p.prorata)} />
                  <Row l={`Indemnité CP (${p.cp_days} j)`} v={eur(p.cp_indemnity)} />
                  {p.notice_amount > 0 && <Row l={`Indemnité de préavis (${p.notice_months} mois)`} v={eur(p.notice_amount)} />}
                  {p.precarity_amount > 0 && <Row l="Prime de précarité CDD" v={eur(p.precarity_amount)} />}
                  {showSeverance && <Row l={`Indemnité de rupture (${p.severance_type.replace("_", " ")})`} v={eur(p.severance_amount)} hint={p.severance_amount > p.severance_legal ? `dont ${eur(p.severance_amount - p.severance_legal)} > légal → CSG/CRDS 9,7 %` : "exonérée de cotisations"} />}
                  <div className="border-t border-white/10 pt-1.5 flex justify-between font-bold text-[#f7c935]"><span>Total indemnités</span><span className="font-mono" data-testid="exit-total">{eur(p.total_indemnities)}</span></div>
                  <div className="text-[10px] text-slate-500 leading-snug pt-1">Code DSN {p.dsn_code}. Le bulletin du mois de sortie ({p.end_day}) reprendra ces montants ; les documents (reçu STC, certificat, récap France Travail) seront générés à sa validation.</div>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => onClose(false)} className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:bg-white/5" data-testid="exit-cancel-btn">Annuler</button>
          <button onClick={save} disabled={saving || !form.end_date} className="px-3 py-2 rounded-lg text-xs font-bold bg-[#f7c935] text-[#0b0811] hover:bg-[#ffd75e] disabled:opacity-50 flex items-center gap-1.5" data-testid="exit-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />} {ex.status ? "Mettre à jour la sortie" : "Planifier la sortie"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ l, v, hint }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-slate-400">{l}{hint && <span className="block text-[10px] text-slate-600">{hint}</span>}</span>
      <span className="font-mono text-white whitespace-nowrap">{v}</span>
    </div>
  );
}
